import { useUnifiedStatus } from "@/contexts/UnifiedStatusContext";
import { UnifiedStatusBadge } from "@/components/shared/UnifiedStatusBadge";
import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Heart, Gift, Star, Clock, Sparkles, PartyPopper, ArrowRight } from "lucide-react";

const MiniLogo = ({ name, size = "md" }) => {
  const colors = ["bg-pink-400", "bg-sky-400", "bg-yellow-400", "bg-green-400", "bg-violet-400", "bg-orange-400", "bg-red-400", "bg-teal-400"];
  const i = name ? name.charCodeAt(0) % colors.length : 0;
  const s = size === "sm" ? "w-9 h-9 text-xs" : size === "lg" ? "w-14 h-14 text-lg" : "w-10 h-10 text-sm";
  return <div className={`${s} rounded-2xl ${colors[i]} flex items-center justify-center flex-shrink-0 border-2 border-slate-900`}><span className="text-slate-900 font-extrabold">{name?.charAt(0)?.toUpperCase()}</span></div>;
};

const ConfettiDot = ({ delay }) => (
  <motion.div
    initial={{ y: -10, opacity: 0 }}
    animate={{ y: [0, -20, 0], opacity: [1, 1, 0], rotate: [0, 180, 360] }}
    transition={{ duration: 2, delay, repeat: Infinity, repeatDelay: 3 }}
    className={`absolute w-2 h-2 rounded-full ${["bg-pink-400", "bg-yellow-400", "bg-sky-400", "bg-green-400", "bg-violet-400"][Math.floor(delay * 5) % 5]}`}
    style={{ left: `${(delay * 137) % 100}%`, top: `${(delay * 89) % 30}%` }}
  />
);

export const UnboxingView = ({ data, user, onOfferClick }) => {
  const { getStatus } = useUnifiedStatus();
  const { offers, expiringSoon, bestValue, stats, favoriteIds, toggleFavorite } = data;
  const totalValue = offers.reduce((s, o) => s + (o.offer_value_est || 0), 0);

  // Pick featured cards for the large bento cells
  const featured = bestValue[0] || offers[0];
  const secondary = bestValue[1] || offers[1];

  // Category quick stats
  const catCounts = {};
  offers.forEach(o => { catCounts[o.category] = (catCounts[o.category] || 0) + 1; });
  const topCats = Object.entries(catCounts).sort((a, b) => b[1] - a[1]).slice(0, 4);

  return (
    <div className="min-h-screen bg-yellow-50 pb-28" style={{ fontFamily: "'Syne', sans-serif" }}>
      {/* Marquee header with confetti */}
      <div className="relative overflow-hidden px-5 pt-8 pb-4">
        {[0.1, 0.3, 0.5, 0.7, 0.9, 1.2, 1.5].map(d => <ConfettiDot key={d} delay={d} />)}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative z-10"
        >
          <h1 className="text-4xl font-extrabold text-slate-900 leading-none tracking-tight">
            Happy<br />Birthday!
          </h1>
          <p className="text-sm text-slate-500 mt-2 font-medium">{stats.days_until_birthday} days to go, {user?.first_name}</p>
        </motion.div>
      </div>

      {/* Bento Grid */}
      <div className="px-5 grid grid-cols-2 gap-3">
        {/* Total value — large */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="col-span-1 bg-white rounded-2xl border-2 border-slate-900 p-4"
          data-testid="unbox-total"
        >
          <Gift className="w-5 h-5 text-pink-500 mb-2" />
          <p className="text-3xl font-extrabold text-slate-900">${totalValue.toFixed(0)}</p>
          <p className="text-xs text-slate-500 font-medium mt-1">Total Loot</p>
        </motion.div>

        {/* Stats block */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="col-span-1 bg-pink-400 rounded-2xl border-2 border-slate-900 p-4 text-white"
          data-testid="unbox-stats"
        >
          <div className="flex justify-between items-start">
            <div>
              <p className="text-3xl font-extrabold">{offers.length}</p>
              <p className="text-xs font-medium opacity-80">Gifts</p>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold">{expiringSoon.length}</p>
              <p className="text-[10px] font-medium opacity-70">Expiring</p>
            </div>
          </div>
        </motion.div>

        {/* Featured — full width */}
        {featured && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            onClick={() => onOfferClick(featured)}
            className="col-span-2 bg-sky-400 rounded-2xl border-2 border-slate-900 p-5 cursor-pointer relative overflow-hidden"
            data-testid="unbox-featured"
          >
            <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/20 rounded-full" />
            <div className="flex items-center gap-4 relative z-10">
              <MiniLogo name={featured.name} size="lg" />
              <div className="flex-1">
                <p className="text-[10px] font-bold text-slate-900/60 uppercase tracking-widest">Best Pick</p>
                <h3 className="text-xl font-extrabold text-slate-900">{featured.name}</h3>
                {getStatus(featured.business_id) && <UnifiedStatusBadge status={getStatus(featured.business_id)} />}
                <p className="text-sm text-slate-900/60 mt-0.5 truncate">{featured.offer_title}</p>
              </div>
              <p className="text-3xl font-extrabold text-slate-900">${featured.offer_value_est}</p>
            </div>
          </motion.div>
        )}

        {/* Category icon tiles */}
        {topCats.map(([cat, count], ci) => {
          const bgColors = ["bg-yellow-300", "bg-green-400", "bg-violet-400", "bg-orange-400"];
          return (
            <motion.div
              key={cat}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + ci * 0.05 }}
              className={`${bgColors[ci]} rounded-2xl border-2 border-slate-900 p-3 cursor-pointer`}
              data-testid={`unbox-cat-${cat.toLowerCase().replace(/\s/g, '-')}`}
            >
              <p className="text-xl font-extrabold text-slate-900">{count}</p>
              <p className="text-[10px] font-bold text-slate-900/60 truncate">{cat}</p>
            </motion.div>
          );
        })}

        {/* Expiring soon — full width list */}
        {expiringSoon.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="col-span-2 bg-red-100 rounded-2xl border-2 border-slate-900 p-4"
            data-testid="unbox-expiring"
          >
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4 text-red-600" />
              <h3 className="text-sm font-extrabold text-slate-900">Expiring Soon!</h3>
            </div>
            <div className="space-y-2">
              {expiringSoon.slice(0, 3).map(offer => (
                <div
                  key={offer.business_id}
                  onClick={() => onOfferClick(offer)}
                  className="flex items-center gap-3 p-2 bg-white rounded-xl border-2 border-slate-900 cursor-pointer hover:bg-yellow-50 transition-colors"
                  data-testid={`unbox-exp-${offer.business_id}`}
                >
                  <MiniLogo name={offer.name} size="sm" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-slate-900 truncate">{offer.name}</p>
                    <p className="text-xs text-slate-500">{offer.category}</p>
                  </div>
                  <p className="text-sm font-extrabold text-slate-900">${offer.offer_value_est}</p>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* All freebies list — full width */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="col-span-2 bg-white rounded-2xl border-2 border-slate-900 p-4"
          data-testid="unbox-all"
        >
          <h3 className="text-sm font-extrabold text-slate-900 mb-3">All Gifts ({offers.length})</h3>
          <div className="space-y-2 max-h-[350px] overflow-y-auto">
            {offers.slice(0, 15).map(offer => (
              <div
                key={offer.business_id}
                onClick={() => onOfferClick(offer)}
                className="flex items-center gap-3 py-2 cursor-pointer group"
                data-testid={`unbox-offer-${offer.business_id}`}
              >
                <MiniLogo name={offer.name} size="sm" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-900 truncate group-hover:text-sky-600 transition-colors">{offer.name}</p>
                  <p className="text-xs text-slate-400">{offer.category}</p>
                </div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-extrabold text-slate-900">${offer.offer_value_est}</p>
                  <button onClick={(e) => { e.stopPropagation(); toggleFavorite(offer.business_id); }} className="p-0.5">
                    <Heart className={`w-3.5 h-3.5 ${favoriteIds.has(offer.business_id) ? "fill-red-500 text-red-500" : "text-slate-300"}`} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};
